import { Injectable } from '@angular/core';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { PermissionsService } from 'src/app/project/modules/share/permissions.service';
import { SiteTablePointsService } from 'src/app/project/modules/site/site-table/site-table-points.service';
import { TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { TPoint, TPointsByWorkspace } from '../points/points.model';
import { PointsService } from '../points/points.service';
import { getSelectedPoints } from '../points/selected-points';
import { CustomTableService } from '../site/site-table/custom-table/custom-table.service';
import { TExportPoints } from './export-body.model';
import { logErrorInSentry } from '../errors/response-error';

@Injectable({
  providedIn: 'root',
})
export class PointExportFilterService {
  constructor(
    private permissionsService: PermissionsService,
    private translationPipe: TranslationPipe,
    private promptService: PromptService,
    private workspaceService: WorkspaceService,
    private pointsService: PointsService,
    private siteTablePointsService: SiteTablePointsService,
    private customTableService: CustomTableService,
  ) {}

  filterPoints(points: TPoint[]): TPoint[] {
    const originalLength = points.length;
    const originalWorkspaceIds = [
      ...new Set(points.map((p) => p.workspaceRef?.id).filter(Boolean)),
    ];

    const blockedWorkspaceIds = originalWorkspaceIds.filter(
      (id) => !this.permissionsService.areExportsEnabled(id),
    );

    points = points.filter((_point) =>
      // Remove points that don't have export enabled in AAL
      this.permissionsService.areExportsEnabled(_point.workspaceRef.id),
    );

    const filteredLength = points.length;

    if (originalLength !== filteredLength) {
      const prompt = this.translationPipe.transform('export_filtered_warning');

      this.promptService.showWarning(prompt);

      logErrorInSentry({
        message: 'Excel export: AAL permissions filtered out points',
        tags: {
          originalCount: String(originalLength),
          filteredCount: String(filteredLength),
          filteredPercentage: String(Math.round((1 - filteredLength / originalLength) * 100)),
          blockedWorkspaceIds: blockedWorkspaceIds.slice(0, 5).join(','),
          blockedWorkspaceCount: String(blockedWorkspaceIds.length),
          totalWorkspaceCount: String(originalWorkspaceIds.length),
        },
      });
    }

    return points;
  }

  filterOverviewPoints(): TExportPoints {
    const allPoints = this.pointsService.getAllPoints();
    const workspaces = this.workspaceService.getWorkspaces();
    const points: TExportPoints = {};
    const selectedPointsIds = getSelectedPoints();

    let selectedPoints: TPoint[] = [];

    Object.keys(allPoints).forEach((workspaceId) => {
      selectedPoints.push(
        ...allPoints[workspaceId].entities.filter((point) => selectedPointsIds.includes(point._id)),
      );
    });

    if (selectedPoints.length > 0) {
      selectedPoints = this.sortSelectedPoints(selectedPoints, points, workspaces);
    } else {
      let visiblePoints = this.customTableService.getTable().getPoints();
      const visiblePointsIds = this.customTableService.getTable().getVisiblePointIds();

      Object.keys(allPoints).forEach((workspaceId) => {
        visiblePoints = [
          ...visiblePoints,
          ...allPoints[workspaceId].entities.filter((point) =>
            visiblePointsIds.includes(point._id),
          ),
        ];
      });

      visiblePoints = this.filterPoints(visiblePoints);

      if (visiblePoints.length > 0) {
        this.sortVisiblePoints(points, workspaces);
      } else {
        this.sortAllPoints(allPoints, points, workspaces);
      }
    }

    return points;
  }

  private sortSelectedPoints(
    selectedPoints: TPoint[],
    points: TExportPoints,
    workspaces: TWorkspacesById,
  ): TPoint[] {
    selectedPoints = this.filterPoints(selectedPoints);

    const sortedSelectedPoints = this.siteTablePointsService.sortPoints(selectedPoints);

    sortedSelectedPoints.forEach((point) => {
      if (!points[point.workspaceRef.id]) {
        points[point.workspaceRef.id] = {};
      }

      if (points[point.workspaceRef.id].points) {
        points[point.workspaceRef.id].points.push(point._id);
      } else {
        points[point.workspaceRef.id].points = [point._id];
      }

      if (!points[point.workspaceRef.id].extent) {
        points[point.workspaceRef.id].extent = this.getWorkspaceExtent(
          workspaces,
          point.workspaceRef.id,
        );
      }
    });

    return selectedPoints;
  }

  private getWorkspaceExtent(workspaces: TWorkspacesById, workspaceId: string): number[] {
    if (workspaces[workspaceId].sitePlan) {
      return workspaces[workspaceId].sitePlan.extent;
    } else {
      return null;
    }
  }

  private sortAllPoints(
    allPoints: TPointsByWorkspace,
    points: TExportPoints,
    workspaces: TWorkspacesById,
  ): void {
    Object.keys(allPoints).forEach((workspaceId) => {
      if (this.permissionsService.areExportsEnabled(workspaceId)) {
        if (!points[workspaceId]) {
          points[workspaceId] = {};
        }

        points[workspaceId].points = allPoints[workspaceId].entities.map((point) => point._id);

        if (!points[workspaceId].extent) {
          points[workspaceId].extent = this.getWorkspaceExtent(workspaces, workspaceId);
        }
      }
    });
  }

  private sortVisiblePoints(points: TExportPoints, workspaces: TWorkspacesById): void {
    const table = this.customTableService.getTable();
    const sortedVisiblePoints = table.getPoints();

    sortedVisiblePoints.forEach((point) => {
      if (this.permissionsService.areExportsEnabled(point.workspaceRef.id)) {
        if (!points[point.workspaceRef.id]) {
          points[point.workspaceRef.id] = {};
        }

        if (points[point.workspaceRef.id].points) {
          points[point.workspaceRef.id].points.push(point._id);
        } else {
          points[point.workspaceRef.id].points = [point._id];
        }

        if (!points[point.workspaceRef.id].extent) {
          points[point.workspaceRef.id].extent = this.getWorkspaceExtent(
            workspaces,
            point.workspaceRef.id,
          );
        }
      }
    });
  }
}
